import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '@services/app/dialog.service';
import { MetaService } from '@services/app/meta.service';
import { OrderDetailService } from '@services/order-detail.service';
import { OrderItemService } from '@services/order-item.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { PortalAuthResponse } from '@schemas/auth.interface';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'tvb-return-portal',
  templateUrl: './return-portal.component.html',
  styleUrls: ['./return-portal.component.scss'],
})
export class ReturnPortalComponent implements OnInit {
  private orderItemId: number;
  private buyerEmail: string;
  private authDetails: PortalAuthResponse;
  public isUserLoggedIn: boolean = false;
  public orderItem: any = null;
  public notFound: boolean = false;
  public showForm: boolean = true;
  public returnForm = new FormGroup({});
  public searchForm = new FormGroup({});
  public recaptcha_site_key = environment.RECAPTCHA_SITE_KEY;

  constructor(
    private orderItemService: OrderItemService,
    private orderDetail: OrderDetailService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private spinnerService: NgxSpinnerService,
    private metaService: MetaService,
    private snackBarService: MatSnackBar
  ) {}

  resolved(captchaResponse: string): void {
    if (this.isUserLoggedIn) return;

    this.authService.returnPortalLogin(captchaResponse).subscribe({
      next: (response: PortalAuthResponse) => {
        if (response && response.status == 'Success') {
          this.authDetails = response;
          this.isUserLoggedIn = true;
        }
        else {
          this.snackBarService.open(
            'Failed to validate captcha response, error: ' + response?.message || 'Unknown error.',
            'Close',
            {
              duration: 5000,
            }
          );
        }
      },
      error: error => {
        this.snackBarService.open(
          'Failed to validate captcha response, error: ' + error?.message || 'Unknown error.',
          'Close',
          {
            duration: 5000,
          }
        );
      },
    });
  }

  ngOnInit(): void {
    this.metaService.setPageNoIndex();
    this.setupForm();
    this.getParameters();
  }

  setupForm() {
    this.searchForm = this.formBuilder.group({
      email: [null, Validators.required],
      orderItemId: [null, Validators.required],
    });
    this.returnForm = this.formBuilder.group({
      reason: [null, Validators.required],
    });
  }

  getParameters() {
    if (this.activatedRoute.snapshot.paramMap.get('id') && this.activatedRoute.snapshot.paramMap.get('buyerEmail')) {
      this.orderItemId = parseInt(this.activatedRoute.snapshot.paramMap.get('id'));
      this.buyerEmail = this.activatedRoute.snapshot.paramMap.get('buyerEmail');
      this.showForm = false;
      this.getOrderItemDetail();
    }
  }

  getOrderItemDetail() {
    this.spinnerService.show();
    this.orderItemService.getOrderItemDetail(this.orderItemId, this.buyerEmail).subscribe(response => {
      this.spinnerService.hide();

      if (response?.data) {
        this.orderItem = response.data;
        this.showForm = false;
        this.notFound = false;
      }
      else this.notFound = true;
    });
  }

  showProduct() {
    this.buyerEmail = this.searchForm.value.email;
    this.orderItemId = this.searchForm.value.orderItemId;
    this.getOrderItemDetail();
  }

  returnOrder() {
    this.dialogService.openTvbDialog({
      title: 'Confirmation',
      messages: [`Are you sure you want to RETURN the order #${this.orderItem.id}?`],
      showActionButtons: true,
    });

    this.dialogService.confirmedTvbDialog().subscribe(confirmed => {
      if (confirmed) {
        const formData = {
          item_id: this.orderItem.id,
          country_id: this.orderItem.countryId,
          return_reason: this.returnForm.value.reason,
          return_policy: true,
        };

        this.spinnerService.show();
        this.orderDetail.savePortalReturn(formData, this.authDetails.data.accessToken).subscribe({
          next: response => {
            if (response?.label_url) {
              this.handleDownloadLabel();
            }
            else {
              this.snackBarService.open(
                'Failed to generate shipping label, error: ' + response?.message || 'Unknown error.',
                'Close',
                {
                  duration: 5000,
                }
              );
              this.spinnerService.hide();
            }
          },
          error: error => {
            this.snackBarService.open(
              'Failed to generate shipping label' + error?.message || 'Unknown error.',
              'Close',
              {
                duration: 5000,
              }
            );
            this.spinnerService.hide();
          },
        });
      }
    });
  }

  handleDownloadLabel() {
    this.orderDetail.getPortalReturnLabel(this.orderItem.id, this.authDetails.data.accessToken).subscribe({
      next: (pdfBlob: Blob) => {
        const url = window.URL.createObjectURL(pdfBlob);

        this.spinnerService.hide();
        window.open(url, '_blank');
      },
      error: error => {
        this.snackBarService.open('Failed to download shipping label' + error?.message || 'Unknown error.', 'Close', {
          duration: 5000,
        });
        this.spinnerService.hide();
      },
    });
  }
}
